<template>
  <nav class="bg-[#11111b] text-white shadow-md p-4 flex justify-between items-center">
    <div class="flex items-center">
      <button @click="$emit('toggle-sidebar')" class="mr-4">
        <MenuIcon class="h-6 w-6 text-white" />
      </button>
    </div>

    <div class="flex items-center space-x-4 z-50">
      <!-- New Client Side Button -->
      <button @click="goToClientSide" class="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md transition">
        Go to Client Side
      </button>

      <div class="relative" ref="dropdownRef">
        <button @click="dropdownOpen = !dropdownOpen" class="flex items-center space-x-2">
          <img :src="currentUser.imageURL || defaultAvatar" alt="User Avatar" class="h-8 w-8 rounded-full" 
            @error="handleImageError" />
          <span>{{ currentUser.displayName || 'User' }}</span>
        </button>
        <div v-if="dropdownOpen" class="absolute right-0 mt-2 w-48 bg-[#11111b] shadow-lg rounded-md">
          <button @click="openProfileModal" class="block w-full text-left px-4 py-2 text-white hover:bg-black">
            Profile
          </button>
          <button @click="logout" class="block w-full text-left px-4 py-2 text-white hover:bg-black">
            Log out
          </button>
        </div>
      </div>
    </div>

    <ProfileForm v-if="showProfileModal" :isVisible="showProfileModal" :userId="auth.currentUser?.uid || ''"
      @onEmitHandleEvent="handleProfileEvent" />
    
    <!-- Toast Notification -->
    <ToastedVue v-if="showToast" :informMessage="toastMessage" :isFailed="toastFailed"
      @onAutoClose="handleCloseToast" />
  </nav>
</template>

<script setup>
import { MenuIcon } from 'lucide-vue-next';
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useAuthStore } from '@/stores/auth';
import defaultAvatar from '@/assets/images/avatar.png';
import ProfileForm from '@/components/ProfileForm.vue';
import ToastedVue from '@/components/ToastedVue.vue';

const dropdownOpen = ref(false);
const dropdownRef = ref(null);
const showProfileModal = ref(false);
const authStore = useAuthStore();
const currentUser = ref({
  displayName: 'User',
  imageURL: null,
});
const showToast = ref(false);
const toastMessage = ref('');
const toastFailed = ref(false);

const auth = getAuth();
const db = getFirestore();
const router = useRouter();

const fetchUserData = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      currentUser.value = {
        displayName: data.displayName || 'User',
        imageURL: data.imageURL || null,
      };
    }
  } catch (error) {
    console.error('Error fetching user data:', error);
  }
};

const handleProfileEvent = (event, error = false, message = '') => {
  if (event === 'close') {
    showProfileModal.value = false;
  } else if (event === 'updated') {
    showProfileModal.value = false;
    showToast.value = true;
    // If message is provided, it's a warning (non-critical update succeeded)
    if (message) {
      toastMessage.value = message;
      toastFailed.value = false; // Show as info/success since update succeeded
    } else {
      toastMessage.value = 'Profile updated successfully!';
      toastFailed.value = false;
    }
    // Refresh user data to show updated profile
    if (auth.currentUser) {
      fetchUserData(auth.currentUser.uid);
    }
  } else if (error) {
    showToast.value = true;
    toastMessage.value = message || 'Failed to update profile. Please try again.';
    toastFailed.value = true;
  }
};

const handleCloseToast = () => {
  showToast.value = false;
  toastMessage.value = '';
  toastFailed.value = false;
};

const handleImageError = (event) => {
  // Fallback to default avatar if image fails to load
  if (event.target.src !== defaultAvatar) {
    event.target.src = defaultAvatar;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  onAuthStateChanged(auth, (user) => {
    if (user) {
      fetchUserData(user.uid);
    } else {
      currentUser.value = { displayName: 'User', imageURL: null };
    }
  });
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    dropdownOpen.value = false;
  }
};

const logout = async () => {
  const result = await authStore.signOutUser();
  if (result.success) {
    console.log('✅ Signed out');
    dropdownOpen.value = false;
    router.push('/home');
  } else {
    console.error('Sign out failed:', result.error);
  }
};

const openProfileModal = () => {
  showProfileModal.value = true;
  dropdownOpen.value = false;
};

// New function to navigate to client side
const goToClientSide = () => {
  router.push('/');
};
</script>